import * as _ from 'underscore';

import { ISchedule } from '../ServerApi/types/ISchedule';
import { ISiteNameWithId } from '../ServerApi/types/ISiteNameWithId';
import { ExplorableItemType, SpaceInfoItemType } from './ContentType';
import { ExtractEncryptionState } from './IDataSourceInfo';
import { IUserInfo } from './IUserInfo';

export interface ICertifiableItemInfo {
  isCertified?: boolean;
  certificationNote?: string;
  certifier?: IUserInfo;
}

export interface ICreatedItemInfo {
  createdAt: Date;
}

export interface IDescriptionItemInfo {
  description: string;
}

export interface IFavoriteItem {
  favorite: boolean;
}

export interface IRecommendationItem {
  hide?: boolean;
  readonly modelId?: string;
  readonly explanationType?: string;
  readonly similiarUsers?: IUserInfo[];
}

export interface FlowItemInfo {
  readonly flowId: string;
}

export interface FlowItemWithGraphImageInfo {
  readonly flowGraphImageUrl?: string;
}

export interface FlowItemWithFileTypeInfo {
  readonly flowFileType: string;
}

export interface FlowItemWithDocumentVersionInfo {
  readonly flowDocumentVersion: string;
}

export interface FlowItemWithThumbnailInfo {
  readonly thumbnailUrl?: string;
}

export interface IIdItemInfo {
  id: string;
}

export interface INamedItemInfo extends IIdItemInfo{
  name?: string;
}

export interface IExplorableItemTypeInfo<C extends ExplorableItemType = ExplorableItemType> extends INamedItemInfo, IVizportalIdItemInfo {
  contentType: C;
}

// SpaceInfo's item must extend this interface
export interface ISpaceTypeInfo<C extends SpaceInfoItemType = SpaceInfoItemType> extends INamedItemInfo {
  contentType: C; // this field is "contentType" rather than "spaceType" so that an IContentInfo can also be a ISpaceTypeInfo
}

// This is currently used only for permissions principals (users vs. groups). For content browsing,
// use the interfaces above.
export interface ITypedItemInfo extends INamedItemInfo {
  type: string;
}

export interface IOwnedItemInfo {
  owner?: IUserInfo;
}

// A contact is different from an owner in that he/she doesn't own the asset (applicable to database & table content types).
export interface IContactItemInfo {
  contact?: IUserInfo;
}

export interface IProjectItemInfo {
  project?: INamedItemInfo;
}

export function isIProjectItemInfo(x: any): x is IProjectItemInfo {
  return x.hasOwnProperty('project');
}

export interface ITaggedItemInfo {
  tags: string[];
}

export interface IThumbnailItemInfo {
  thumbnailUrl: string;
}

export interface IDownloadItemInfo {
  downloadUrl: string;
}

export interface IUpdatedItemInfo {
  updatedAt: Date;
}

export interface IEditableItemInfo {
  // This property will be undefined if the workbook is not editable, example: workbooks with stories
  editUrl?: string;
}

export interface ITabbedItemInfo {
  displayTabs: boolean;
}

export interface IItemStatisticsInfo<T extends _.Dictionary<number>> {
  usageInfo: T; // The statistics for this particular item
  usageInfoMaxValues: T; // The maximum values of each statistic across all items in the relevant set
}

export interface IStatsTimeSeriesInfo {
  hitsTimeSeries?: number[];
}

export interface IEncryptableExtractItemInfo {
  hasExtracts: boolean;
  extractEncryptionState: ExtractEncryptionState;
  hasEncryptableExtracts: boolean;
}

export interface IExtractItemInfo extends IEncryptableExtractItemInfo {
  lastRefreshedAt?: Date;
  hasAlert: boolean;
  hasIncrementalExtract: boolean;
  extractCreationPending: boolean;
}

export interface IScheduledItemInfo {
  schedule: ISchedule;
}

export interface ISiteItemInfo {
  site: ISiteNameWithId;
}

export interface INamedChildItemInfo extends INamedItemInfo {
  parentId: string;
}

// TFS912863: Remove this hack once we switched to use vizportalId as primary ID for database & table
export interface IVizportalIdItemInfo {
  vizportalId?: string;
}

export interface IControlledPermissionItemInfo extends INamedItemInfo, IIdItemInfo{
  controlledPermissionsEnabled: boolean;
}

export interface IRelatedContentInfo {
  relatedContentId: string;
  relatedContentType: ExplorableItemType;
}
